import { useQuery } from "@tanstack/react-query";
import { PageHeader, Badge } from "@/components/ui/PageHeader";
import {
  getNotificationAnalytics,
  getFunnelAnalytics,
  type FunnelReport,
  type NotificationTypeStats,
} from "@/api/admin";

export default function AnalyticsLab() {
  const { data: notif, isLoading: nLoading } = useQuery({
    queryKey: ["admin-analytics-notifications"],
    queryFn: () => getNotificationAnalytics(),
  });

  const { data: funnels, isLoading: fLoading } = useQuery({
    queryKey: ["admin-analytics-funnels"],
    queryFn: () => getFunnelAnalytics(),
  });

  return (
    <div className="space-y-8">
      <PageHeader
        title="Analytics"
        subtitle="Notification performance + engagement funnel drop-off (Phase 1D.1). Default window: last 30 days."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Analytics" },
        ]}
      />

      {/* Notification analytics */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Notification performance
        </h2>
        {nLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !notif ? (
          <p className="text-sm text-gray-500">No data.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Stat label="Sent" value={notif.data.totals.sent} />
              <Stat
                label="Delivered"
                value={`${notif.data.totals.delivered} (${pct(notif.data.totals.deliveryRate)})`}
              />
              <Stat
                label="Opened"
                value={`${notif.data.totals.opened} (${pct(notif.data.totals.openRate)})`}
              />
              <Stat
                label="Tapped"
                value={`${notif.data.totals.tapped} (${pct(notif.data.totals.tapRate)})`}
              />
            </div>

            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              By type
            </h3>
            {notif.data.byType.length === 0 ? (
              <p className="text-sm text-gray-500">
                No notifications in window.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-gray-500 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-2">Type</th>
                      <th className="text-right py-2">Sent</th>
                      <th className="text-right py-2">Delivery</th>
                      <th className="text-right py-2">Open</th>
                      <th className="text-right py-2">Tap</th>
                      <th className="text-right py-2">Avg → Open</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {notif.data.byType.map((t) => (
                      <TypeRow key={t.type} t={t} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      {/* Funnel analytics */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Engagement funnels
        </h2>
        {fLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !funnels || funnels.data.funnels.length === 0 ? (
          <p className="text-sm text-gray-500">No funnel data yet.</p>
        ) : (
          <div className="space-y-6">
            {funnels.data.funnels.map((f) => (
              <FunnelChart key={f.funnelName} report={f} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function pct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}

function Stat({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function TypeRow({ t }: { t: NotificationTypeStats }) {
  const avgMs = t.avgTimeToOpenMs;
  const avgLabel =
    avgMs == null
      ? "—"
      : avgMs < 60_000
        ? `${Math.round(avgMs / 1000)}s`
        : avgMs < 3_600_000
          ? `${Math.round(avgMs / 60_000)}m`
          : `${(avgMs / 3_600_000).toFixed(1)}h`;
  return (
    <tr className="text-gray-700">
      <td className="py-2">
        <code className="font-mono text-xs">{t.type}</code>
      </td>
      <td className="py-2 text-right tabular-nums">{t.sent}</td>
      <td className="py-2 text-right tabular-nums">{pct(t.deliveryRate)}</td>
      <td className="py-2 text-right tabular-nums">{pct(t.openRate)}</td>
      <td className="py-2 text-right tabular-nums">{pct(t.tapRate)}</td>
      <td className="py-2 text-right tabular-nums">{avgLabel}</td>
    </tr>
  );
}

function FunnelChart({ report }: { report: FunnelReport }) {
  const maxUsers = Math.max(
    1,
    ...report.steps.map((s) => s.uniqueUsers),
  );
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">
          <code className="font-mono text-sm">{report.funnelName}</code>
        </h3>
        <Badge variant="gray">{report.steps.length} steps</Badge>
      </div>
      <ol className="space-y-2">
        {report.steps.map((step, idx) => (
          <li key={step.step}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-700">
                <span className="text-gray-400 font-mono mr-2">
                  {idx + 1}.
                </span>
                {step.step}
              </span>
              <span className="text-xs text-gray-500 tabular-nums">
                {step.uniqueUsers} users · {step.conversionFromFirstStepPct}%
                from start
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all"
                style={{
                  width: `${(step.uniqueUsers / maxUsers) * 100}%`,
                }}
              />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
